import type {
  ChipSalesObservation,
  ChipSalesQualityReport,
  Quantiles,
} from "../dataSources/chipSales";

export const COMPUTE_METRIC_IDS = [
  "chip_spend_usd",
  "delivered_compute_index",
  "new_it_nameplate_mw",
] as const;

export const COMPUTE_DESIGNERS = [
  "Nvidia",
  "AMD",
  "Google",
  "Amazon",
  "Huawei",
  "Cambricon",
] as const;

export type ComputeMetricId = typeof COMPUTE_METRIC_IDS[number];
export type ComputeDesigner = typeof COMPUTE_DESIGNERS[number];
export type ComputeForecastMethod = "lag-1" | "seasonal-lag-4" | "drift";
export type ComputeModelStatus = "production-baseline" | "research-baseline";

export interface ComputeMetricSnapshot {
  id: ComputeMetricId;
  label: string;
  unit: string;
  value: number;
  low: number;
  high: number;
  observationCount: number;
  missingComponentCount: number;
  coveragePct: number;
}

export interface ComputeDesignerMonitor {
  designer: ComputeDesigner;
  watermark: string | null;
  latestObservedPeriod: string | null;
  lastModified: string | null;
  incompleteRecords: number;
  currentIncompleteRecords: number;
  productionRecords: number;
  metrics: Record<ComputeMetricId, number | null>;
}

export interface ComputeSupplyOriginPoint {
  period: string;
  /** Delivered-compute index contribution from China-HQ designers (headline index scale). */
  chinaIndex: number;
  /** Contribution from all other designers (currently all US-HQ). */
  otherIndex: number;
  chinaSharePct: number;
}

export interface ComputeSupplyOrigin {
  /** Monitor evidence from median sums; never a forecast input. */
  basis: string;
  series: ComputeSupplyOriginPoint[];
  latest: ComputeSupplyOriginPoint;
}

export interface ComputeForecastPoint {
  period: string;
  horizon: 1 | 2 | 3 | 4;
  predicted: number;
  low: number;
  high: number;
  intervalSource: "measurement-plus-oos-simulation";
}

export interface ComputeBacktestPoint {
  period: string;
  actual: number;
  predicted: number;
  error: number;
  absoluteError: number;
  absolutePercentageError: number | null;
}

export interface ComputeCandidateMetrics {
  id: ComputeForecastMethod;
  observations: number;
  mae: number;
  rmse: number;
  mape: number;
  points: ComputeBacktestPoint[];
}

export interface ComputeMetricBacktest {
  metricId: ComputeMetricId;
  selectedModel: ComputeForecastMethod;
  status: ComputeModelStatus;
  statusReason: string;
  observations: number;
  skillVsLag1Pct: number;
  candidates: ComputeCandidateMetrics[];
  selectedPoints: ComputeBacktestPoint[];
}

export interface ComputeDeploymentOptions {
  quality?: ChipSalesQualityReport;
  seed?: number;
  monteCarloSamples?: number;
  includeIncomplete?: boolean;
  incompleteUncertaintyMultiplier?: number;
  /** Publication/run as-of. Source Last-Modified remains a separate watermark. */
  asOf?: string;
}

export interface ComputeDeploymentSliceResult {
  monitor: {
    period: string;
    asOf: string;
    sourceWatermark: string;
    metrics: ComputeMetricSnapshot[];
    designers: ComputeDesignerMonitor[];
    /** Absent when the ingested panel predates designer-country annotation. */
    supplyOrigin?: ComputeSupplyOrigin;
    includedIncompleteCount: number;
    quality: {
      sourceRows: number;
      productionEligibleRows: number;
      incompleteRows: number;
      missingPriceRows: number;
      missingTdpRows: number;
      latestObservedQuarter: string;
      latestProductionQuarter: string;
    };
  };
  forecast: {
    originPeriod: string;
    paths: Record<ComputeMetricId, ComputeForecastPoint[]>;
    selectedModels: Record<ComputeMetricId, ComputeForecastMethod>;
  };
  backtest: {
    metrics: Record<ComputeMetricId, ComputeMetricBacktest>;
  };
  methodology: {
    productionEligible: boolean;
    targetDefinitions: Record<ComputeMetricId, string>;
    estimator: string;
    validation: string;
    uncertainty: string;
    incompletePolicy: string;
    cumulativeReconciliationPolicy: string;
  };
  sources: Array<{
    id: string;
    label: string;
    url: string;
    asOf: string;
    cadence: string;
    license: string;
    role: string;
  }>;
}

interface InternalMetricValue {
  id: ComputeMetricId;
  value: number;
  low: number;
  high: number;
  observationCount: number;
  missingComponentCount: number;
  coveragePct: number;
}

interface InternalQuarter {
  period: string;
  metrics: Record<ComputeMetricId, InternalMetricValue>;
}

const METRIC_META: Record<ComputeMetricId, { label: string; unit: string }> = {
  chip_spend_usd: { label: "芯片支出", unit: "USD B" },
  delivered_compute_index: { label: "交付算力指数", unit: "Index" },
  new_it_nameplate_mw: { label: "新增 IT 铭牌功率", unit: "MW" },
};

function hashText(value: string) {
  let hash = 2_166_136_261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16_777_619);
  }
  return hash >>> 0;
}

function seededRandom(seed: number) {
  let state = seed >>> 0 || 0x9e3779b9;
  return () => {
    state ^= state << 13;
    state ^= state >>> 17;
    state ^= state << 5;
    return (state >>> 0) / 4_294_967_296;
  };
}

function percentile(values: number[], probability: number) {
  if (!values.length) throw new Error("Cannot calculate a percentile from an empty sample.");
  const sorted = [...values].sort((left, right) => left - right);
  const position = (sorted.length - 1) * probability;
  const lower = Math.floor(position);
  const upper = Math.ceil(position);
  const weight = position - lower;
  return sorted[lower] * (1 - weight) + sorted[upper] * weight;
}

function sampleQuantiles(distribution: Quantiles, probability: number, widening: number) {
  const low = Math.max(0, distribution.p50 - (distribution.p50 - distribution.p05) * widening);
  const high = distribution.p50 + (distribution.p95 - distribution.p50) * widening;
  if (probability <= 0.05) return low;
  if (probability <= 0.5) return low + ((distribution.p50 - low) * (probability - 0.05)) / 0.45;
  if (probability <= 0.95) return distribution.p50 + ((high - distribution.p50) * (probability - 0.5)) / 0.45;
  return high;
}

/** Uses a shared draw across chip rows: a conservative, reproducible dependence structure. */
function aggregateDistributions(
  distributions: Array<{ value: Quantiles; incomplete: boolean }>,
  seed: number,
  samples: number,
  incompleteMultiplier: number,
) {
  if (!distributions.length) return null;
  const random = seededRandom(seed);
  const draws = Array.from({ length: samples }, () => {
    const commonProbability = random();
    return distributions.reduce(
      (sum, item) => sum + sampleQuantiles(item.value, commonProbability, item.incomplete ? incompleteMultiplier : 1),
      0,
    );
  });
  const point = distributions.reduce((sum, item) => sum + item.value.p50, 0);
  return {
    p05: Math.min(point, percentile(draws, 0.05)),
    p50: point,
    p95: Math.max(point, percentile(draws, 0.95)),
  };
}

function metricDistributions(metricId: ComputeMetricId, rows: ChipSalesObservation[]) {
  return rows.flatMap((row) => {
    if (metricId === "chip_spend_usd") {
      return row.chipSpendUsd ? [{ value: row.chipSpendUsd, incomplete: row.incomplete }] : [];
    }
    if (metricId === "new_it_nameplate_mw") {
      return row.totalTdpW ? [{ value: row.totalTdpW, incomplete: row.incomplete }] : [];
    }
    return [{ value: row.sourceCompute, incomplete: row.incomplete }];
  });
}

function hasMetric(metricId: ComputeMetricId, row: ChipSalesObservation) {
  if (metricId === "chip_spend_usd") return row.chipSpendUsd != null;
  if (metricId === "new_it_nameplate_mw") return row.totalTdpW != null;
  return true;
}

function publicScale(metricId: ComputeMetricId, value: number, computeBase: number) {
  if (metricId === "chip_spend_usd") return value / 1_000_000_000;
  if (metricId === "new_it_nameplate_mw") return value / 1_000_000;
  return (value / computeBase) * 100;
}

function aggregateQuarter(
  period: string,
  rows: ChipSalesObservation[],
  computeBase: number,
  seed: number,
  samples: number,
  incompleteMultiplier: number,
): InternalQuarter {
  const metrics = Object.fromEntries(COMPUTE_METRIC_IDS.map((metricId) => {
    const distributions = metricDistributions(metricId, rows);
    const aggregate = aggregateDistributions(
      distributions,
      seed ^ hashText(`${period}|${metricId}`),
      samples,
      incompleteMultiplier,
    );
    if (!aggregate) throw new Error(`${period}: ${metricId} has no measurable observations.`);
    const measurableDesigners = new Set(
      rows.filter((row) => hasMetric(metricId, row) && COMPUTE_DESIGNERS.includes(row.designer as ComputeDesigner))
        .map((row) => row.designer),
    );
    const absentDesignerCount = COMPUTE_DESIGNERS.length - measurableDesigners.size;
    const missingComponentCount = rows.length - distributions.length + absentDesignerCount;
    return [metricId, {
      id: metricId,
      value: publicScale(metricId, aggregate.p50, computeBase),
      low: publicScale(metricId, aggregate.p05, computeBase),
      high: publicScale(metricId, aggregate.p95, computeBase),
      observationCount: distributions.length,
      missingComponentCount,
      coveragePct: (measurableDesigners.size / COMPUTE_DESIGNERS.length) * 100,
    } satisfies InternalMetricValue];
  })) as Record<ComputeMetricId, InternalMetricValue>;
  return { period, metrics };
}

function nextQuarter(period: string, offset: number) {
  const match = /^(\d{4})-Q([1-4])$/.exec(period);
  if (!match) throw new Error(`Invalid quarter: ${period}`);
  const absolute = Number(match[1]) * 4 + Number(match[2]) - 1 + offset;
  return `${Math.floor(absolute / 4)}-Q${(absolute % 4) + 1}`;
}

function ensureContiguous(periods: string[]) {
  for (let index = 1; index < periods.length; index += 1) {
    if (periods[index] !== nextQuarter(periods[index - 1], 1)) {
      throw new Error(`Compute deployment series has a calendar gap: ${periods[index - 1]} → ${periods[index]}.`);
    }
  }
}

function predictOneStep(method: ComputeForecastMethod, values: number[]) {
  if (!values.length) throw new Error("Forecast training window is empty.");
  if (method === "lag-1") return values.at(-1)!;
  if (method === "seasonal-lag-4") {
    if (values.length < 4) throw new Error("seasonal-lag-4 requires four prior quarters.");
    return values.at(-4)!;
  }
  if (values.length < 2) return values.at(-1)!;
  return Math.max(0, values.at(-1)! + (values.at(-1)! - values[0]) / (values.length - 1));
}

function futurePrediction(method: ComputeForecastMethod, values: number[], horizon: number) {
  if (method === "lag-1") return values.at(-1)!;
  if (method === "seasonal-lag-4") return values[values.length - 4 + horizon - 1];
  const drift = values.length > 1 ? (values.at(-1)! - values[0]) / (values.length - 1) : 0;
  return Math.max(0, values.at(-1)! + drift * horizon);
}

function candidateBacktest(method: ComputeForecastMethod, periods: string[], values: number[]): ComputeCandidateMetrics {
  const points: ComputeBacktestPoint[] = [];
  for (let target = 4; target < values.length; target += 1) {
    const predicted = predictOneStep(method, values.slice(0, target));
    const actual = values[target];
    const error = predicted - actual;
    points.push({
      period: periods[target],
      actual,
      predicted,
      error,
      absoluteError: Math.abs(error),
      absolutePercentageError: actual === 0 ? null : Math.abs(error / actual) * 100,
    });
  }
  if (!points.length) throw new Error("Compute deployment backtest needs at least five contiguous quarters.");
  const percentageErrors = points.flatMap((point) => point.absolutePercentageError == null ? [] : [point.absolutePercentageError]);
  return {
    id: method,
    observations: points.length,
    mae: points.reduce((sum, point) => sum + point.absoluteError, 0) / points.length,
    rmse: Math.sqrt(points.reduce((sum, point) => sum + point.error ** 2, 0) / points.length),
    mape: percentageErrors.length ? percentageErrors.reduce((sum, value) => sum + value, 0) / percentageErrors.length : 0,
    points,
  };
}

function backtestMetric(metricId: ComputeMetricId, quarters: InternalQuarter[], includeIncomplete: boolean): ComputeMetricBacktest {
  const periods = quarters.map((quarter) => quarter.period);
  const values = quarters.map((quarter) => quarter.metrics[metricId].value);
  const methods: ComputeForecastMethod[] = ["lag-1", "seasonal-lag-4", "drift"];
  const candidates = methods.map((method) => candidateBacktest(method, periods, values));
  const selected = [...candidates].sort((left, right) => left.rmse - right.rmse)[0];
  const lag1 = candidates[0];
  const skillVsLag1Pct = lag1.rmse === 0 ? 0 : ((lag1.rmse - selected.rmse) / lag1.rmse) * 100;
  const coverage = quarters.reduce((sum, quarter) => sum + quarter.metrics[metricId].coveragePct, 0) / quarters.length;
  const productionEligible = !includeIncomplete && selected.observations >= 9 && coverage >= 90;
  return {
    metricId,
    selectedModel: selected.id,
    status: productionEligible ? "production-baseline" : "research-baseline",
    statusReason: productionEligible
      ? `Selected only on ${selected.observations} common rolling out-of-sample points; average measurable coverage ${coverage.toFixed(1)}%.`
      : `Research only: ${selected.observations} common OOS points, ${coverage.toFixed(1)}% average measurable coverage, incomplete expansion=${includeIncomplete}.`,
    observations: selected.observations,
    skillVsLag1Pct,
    candidates,
    selectedPoints: selected.points,
  };
}

function simulatedInterval(
  point: number,
  horizon: number,
  latest: InternalMetricValue,
  residuals: number[],
  seed: number,
  samples: number,
) {
  const random = seededRandom(seed);
  const distribution = { p05: latest.low, p50: latest.value, p95: latest.high };
  const draws = Array.from({ length: samples }, () => {
    const measurement = latest.value === 0
      ? 0
      : (sampleQuantiles(distribution, random(), 1) / latest.value - 1) * point;
    const residual = residuals.length ? residuals[Math.floor(random() * residuals.length)] * Math.sqrt(horizon) : 0;
    return Math.max(0, point + measurement + residual);
  });
  return {
    low: Math.min(point, percentile(draws, 0.05)),
    high: Math.max(point, percentile(draws, 0.95)),
  };
}

function qualityFrom(observations: ChipSalesObservation[], provided?: ChipSalesQualityReport) {
  const quarters = observations.map((row) => row.quarter).sort();
  const productionQuarters = observations.filter((row) => row.productionEligible).map((row) => row.quarter).sort();
  const modified = observations.map((row) => row.lastModified).filter(Boolean).sort();
  return provided ?? {
    totalCount: observations.length,
    productionEligibleCount: observations.filter((row) => row.productionEligible).length,
    incompleteCount: observations.filter((row) => row.incomplete).length,
    missingPriceCount: observations.filter((row) => row.chipSpendUsd == null).length,
    missingTdpCount: observations.filter((row) => row.totalTdpW == null).length,
    estimatedPriceCount: observations.filter((row) => row.priceEstimated).length,
    estimatedTdpCount: observations.filter((row) => row.tdpEstimated).length,
    latestObservedQuarter: quarters.at(-1)!,
    latestCompleteQuarter: productionQuarters.at(-1)!,
    lastModified: modified.at(-1) ?? "unknown",
    issues: [],
  } satisfies ChipSalesQualityReport;
}

function designerMonitor(
  designer: ComputeDesigner,
  observations: ChipSalesObservation[],
  included: ChipSalesObservation[],
  monitorPeriod: string,
  computeBase: number,
  seed: number,
  samples: number,
  incompleteMultiplier: number,
): ComputeDesignerMonitor {
  const allDesignerRows = observations.filter((row) => row.designer === designer);
  const includedDesignerRows = included.filter((row) => row.designer === designer);
  const current = includedDesignerRows.filter((row) => row.quarter === monitorPeriod);
  const periods = includedDesignerRows.map((row) => row.quarter).sort();
  const observedPeriods = allDesignerRows.map((row) => row.quarter).sort();
  const modified = allDesignerRows.map((row) => row.lastModified).filter(Boolean).sort();
  const values = Object.fromEntries(COMPUTE_METRIC_IDS.map((metricId) => {
    const distributions = metricDistributions(metricId, current);
    const aggregate = aggregateDistributions(
      distributions,
      seed ^ hashText(`${monitorPeriod}|${designer}|${metricId}`),
      samples,
      incompleteMultiplier,
    );
    return [metricId, aggregate ? publicScale(metricId, aggregate.p50, computeBase) : null];
  })) as Record<ComputeMetricId, number | null>;
  return {
    designer,
    watermark: periods.at(-1) ?? null,
    latestObservedPeriod: observedPeriods.at(-1) ?? null,
    lastModified: modified.at(-1) ?? null,
    incompleteRecords: allDesignerRows.filter((row) => row.incomplete).length,
    currentIncompleteRecords: allDesignerRows.filter((row) => row.quarter === monitorPeriod && row.incomplete).length,
    productionRecords: allDesignerRows.filter((row) => row.productionEligible).length,
    metrics: values,
  };
}

/**
 * Median-sum split of delivered compute by designer HQ country. Returns
 * undefined when any included row lacks a country annotation (pre-annotation
 * panels), so the UI shows an explicit absence instead of a fabricated zero.
 */
function supplyOriginSeries(
  included: ChipSalesObservation[],
  periods: string[],
  computeBase: number,
): ComputeSupplyOrigin | undefined {
  if (included.some((row) => row.designerCountry == null)) return undefined;
  const series = periods.map((period): ComputeSupplyOriginPoint => {
    const rows = included.filter((row) => row.quarter === period);
    const china = rows.filter((row) => row.designerCountry === "China")
      .reduce((sum, row) => sum + row.sourceCompute.p50, 0);
    const total = rows.reduce((sum, row) => sum + row.sourceCompute.p50, 0);
    return {
      period,
      chinaIndex: (china / computeBase) * 100,
      otherIndex: ((total - china) / computeBase) * 100,
      chinaSharePct: total > 0 ? (china / total) * 100 : 0,
    };
  });
  return {
    basis: "production-eligible sourceCompute p50 sums by designer HQ country (organizations.csv); measurement quantiles are not propagated",
    series,
    latest: series.at(-1)!,
  };
}

/**
 * Deep, side-effect-free module for monitoring deliveries and forecasting the
 * public spend / indexed-compute / nameplate-power targets.
 */
export function runComputeDeploymentSlice(
  observations: ChipSalesObservation[],
  options: ComputeDeploymentOptions = {},
): ComputeDeploymentSliceResult {
  if (!observations.length) throw new Error("Compute deployment slice requires chip sales observations.");
  const seed = options.seed ?? 20_260_713;
  const samples = options.monteCarloSamples ?? 2_048;
  if (!Number.isInteger(samples) || samples < 500) throw new Error("monteCarloSamples must be an integer of at least 500.");
  const includeIncomplete = options.includeIncomplete ?? false;
  const incompleteMultiplier = options.incompleteUncertaintyMultiplier ?? 1.5;
  if (!Number.isFinite(incompleteMultiplier) || incompleteMultiplier < 1) {
    throw new Error("incompleteUncertaintyMultiplier must be at least 1.");
  }
  const included = observations.filter((row) => includeIncomplete || row.productionEligible);
  if (!included.length) throw new Error("No observations satisfy the requested incomplete-record policy.");

  const groups = new Map<string, ChipSalesObservation[]>();
  for (const row of included) groups.set(row.quarter, [...(groups.get(row.quarter) ?? []), row]);
  const periods = [...groups.keys()].sort();
  ensureContiguous(periods);
  if (periods.length < 13) throw new Error("Compute deployment slice needs at least 13 contiguous quarters for OOS status evaluation.");

  const firstRows = groups.get(periods[0])!;
  const baseDistribution = aggregateDistributions(
    metricDistributions("delivered_compute_index", firstRows),
    seed ^ hashText("compute-base"),
    samples,
    incompleteMultiplier,
  );
  if (!baseDistribution || baseDistribution.p50 <= 0) throw new Error("Delivered-compute index base quarter is unavailable.");
  const computeBase = baseDistribution.p50;
  const quarters = periods.map((period) => aggregateQuarter(
    period,
    groups.get(period)!,
    computeBase,
    seed,
    samples,
    incompleteMultiplier,
  ));
  const latest = quarters.at(-1)!;
  const backtests = Object.fromEntries(COMPUTE_METRIC_IDS.map((metricId) => [
    metricId,
    backtestMetric(metricId, quarters, includeIncomplete),
  ])) as Record<ComputeMetricId, ComputeMetricBacktest>;

  const paths = Object.fromEntries(COMPUTE_METRIC_IDS.map((metricId) => {
    const values = quarters.map((quarter) => quarter.metrics[metricId].value);
    const backtest = backtests[metricId];
    const residuals = backtest.selectedPoints.map((point) => point.actual - point.predicted);
    const path = ([1, 2, 3, 4] as const).map((horizon): ComputeForecastPoint => {
      const predicted = futurePrediction(backtest.selectedModel, values, horizon);
      const interval = simulatedInterval(
        predicted,
        horizon,
        latest.metrics[metricId],
        residuals,
        seed ^ hashText(`${metricId}|forecast|${horizon}`),
        samples,
      );
      return {
        period: nextQuarter(latest.period, horizon),
        horizon,
        predicted,
        low: interval.low,
        high: interval.high,
        intervalSource: "measurement-plus-oos-simulation",
      };
    });
    return [metricId, path];
  })) as Record<ComputeMetricId, ComputeForecastPoint[]>;

  const quality = qualityFrom(observations, options.quality);
  const includedIncompleteCount = included.filter((row) => row.incomplete).length;
  const monitorMetrics = COMPUTE_METRIC_IDS.map((metricId): ComputeMetricSnapshot => ({
    ...latest.metrics[metricId],
    label: METRIC_META[metricId].label,
    unit: METRIC_META[metricId].unit,
  }));
  const asOf = options.asOf ?? "unknown";
  if (asOf !== "unknown" && !/^\d{4}-\d{2}-\d{2}$/.test(asOf)) {
    throw new Error(`Compute deployment asOf must use YYYY-MM-DD: ${asOf}`);
  }

  return {
    monitor: {
      period: latest.period,
      asOf,
      sourceWatermark: quality.lastModified,
      metrics: monitorMetrics,
      designers: COMPUTE_DESIGNERS.map((designer) => designerMonitor(
        designer,
        observations,
        included,
        latest.period,
        computeBase,
        seed,
        samples,
        incompleteMultiplier,
      )),
      supplyOrigin: supplyOriginSeries(included, periods, computeBase),
      includedIncompleteCount,
      quality: {
        sourceRows: quality.totalCount,
        productionEligibleRows: quality.productionEligibleCount,
        incompleteRows: quality.incompleteCount,
        missingPriceRows: quality.missingPriceCount,
        missingTdpRows: quality.missingTdpCount,
        latestObservedQuarter: quality.latestObservedQuarter,
        latestProductionQuarter: quality.latestCompleteQuarter,
      },
    },
    forecast: {
      originPeriod: latest.period,
      paths,
      selectedModels: Object.fromEntries(COMPUTE_METRIC_IDS.map((metricId) => [
        metricId,
        backtests[metricId].selectedModel,
      ])) as Record<ComputeMetricId, ComputeForecastMethod>,
    },
    backtest: { metrics: backtests },
    methodology: {
      productionEligible: !includeIncomplete,
      targetDefinitions: {
        chip_spend_usd: "Quarterly estimated accelerator purchase value in USD billions; missing prices remain missing and reduce disclosed coverage.",
        delivered_compute_index: "Quarterly delivered accelerator compute normalized to 100 in the first production-eligible quarter.",
        new_it_nameplate_mw: "Quarterly delivered accelerator chip TDP divided by one million; excludes facility overhead and utilization assumptions.",
      },
      estimator: "For each target, select lag-1, seasonal lag-4, or drift using only common rolling-origin OOS RMSE; emit recursive 1Q–4Q paths.",
      validation: "All candidates use the same calendar-contiguous forecast origins. Production status requires at least nine OOS points and at least 90% average measurable component coverage.",
      uncertainty: `Deterministic ${samples}-draw Monte Carlo with a conservative shared rank across chip rows, then measurement uncertainty plus empirical OOS residual simulation. Seed=${seed}.`,
      incompletePolicy: includeIncomplete
        ? `Research expansion includes source-incomplete rows and widens their source ranges by ${incompleteMultiplier.toFixed(2)}×.`
        : "Production path excludes every source-incomplete row; no missing price or TDP is converted to zero.",
      cumulativeReconciliationPolicy: "Cumulative exports may be used for reconciliation only and are never treated as a second truth source.",
    },
    sources: [{
      id: "epoch-ai-chip-sales",
      label: "Epoch AI · Data on AI Chip Sales",
      url: "https://epoch.ai/data/ai-chip-sales",
      asOf,
      cadence: "Quarterly source reconstruction; record-level updates",
      license: "CC BY 4.0",
      role: "Row-level units, internal compute conversion, TDP, price, uncertainty, and incomplete flags",
    }],
  };
}
